"use client";
import React, { useState, useEffect } from "react";
import {
  Users,
  UserCheck,
  Clock,
  TrendingUp,
  Activity,
  Mars,
  Venus,
  IndianRupee,
} from "lucide-react";

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Stack,
  Paper,
  useMediaQuery,
  useTheme,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
} from "@mui/material";

import apiEndpoints from "../../apiconfig";

export default function Dashboard() {
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiEndpoints.count);

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          setApiData(data.data);
        } else {
          throw new Error(data.error || "Failed to fetch data");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatNumber = (num) => new Intl.NumberFormat("en-IN").format(num);

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInHours = Math.floor(
      (now.getTime() - time.getTime()) / (1000 * 60 * 60),
    );
    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "inactive":
        return "error";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
          flexDirection: "column",
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Error loading dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {error}
        </Typography>
      </Box>
    );
  }

  // Helper for Stats Cards
  const StatCard = ({
    title,
    value,
    subtext,
    icon: Icon,
    color,
    subtextColor,
  }) => (
    <Card sx={{ height: "100%", position: "relative", overflow: "visible" }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={500}
              gutterBottom
            >
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              bgcolor: `${color}15`, // 15% opacity
              color: color,
            }}
          >
            <Icon size={24} />
          </Box>
        </Stack>
        <Typography
          variant="caption"
          sx={{
            color: subtextColor || "text.secondary",
            mt: 2,
            display: "block",
          }}
        >
          {subtext}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ width: "100%", pb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Overview
          </Typography>
          <Typography color="text.secondary">
            Welcome back! Here's what's happening today.
          </Typography>
        </Box>
        <Chip
          label="Live Updates"
          color="secondary"
          icon={<Activity size={16} />}
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Box>

      {/* Primary Stats Grid */}
      <Grid
        container
        spacing={3}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Total Users"
            value={formatNumber(apiData?.total_users)}
            subtext={`${Math.round(
              (apiData?.active_users / apiData?.total_users) * 100,
            )}% active users`}
            icon={Users}
            color={theme.palette.primary.main}
            subtextColor="success.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Active Profiles"
            value={formatNumber(apiData?.active_users)}
            subtext={`${(
              (apiData?.active_users / apiData?.total_users) *
              100
            ).toFixed(1)}% of total`}
            icon={UserCheck}
            color="#10b981" // emerald
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Pending Approvals"
            value={formatNumber(apiData?.not_verified_users)}
            subtext="Requires attention"
            icon={Clock}
            color="#f59e0b" // amber
            subtextColor="warning.main"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Premium Revenue"
            value="40,000"
            subtext="Premium subscriptions"
            icon={IndianRupee}
            color={theme.palette.secondary.main} // Gold
          />
        </Grid>
      </Grid>

      {/* Gender & Trending Stats */}
      <Grid
        container
        spacing={3}
        sx={{ mb: 4, justifyContent: "space-between" }}
      >
        {apiData?.gender?.map((stat, index) => {
          let Icon = UserCheck;
          if (stat.icon === "Mars") Icon = Mars;
          if (stat.icon === "Venus") Icon = Venus;
          if (stat.icon === "TrendingUp") Icon = TrendingUp;

          return (
            <Grid key={stat.title || index} size={{ xs: 12, sm: 6, md: 4 }}>
              <Card
                sx={{
                  bgcolor: stat.bgColor, // Keep original colors for contrast, or map to theme
                  color: "#fff",
                  position: "relative",
                  overflow: "hidden",
                  height: "100%",
                  boxShadow: 3,
                }}
              >
                <CardContent sx={{ position: "relative", zIndex: 1, py: 3 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight={500}
                        sx={{ opacity: 0.9 }}
                      >
                        {stat.title}
                      </Typography>
                      <Typography variant="h3" fontWeight={700} sx={{ mt: 1 }}>
                        {formatNumber(stat.value)}
                      </Typography>
                    </Box>
                    <Box sx={{ opacity: 0.8 }}>
                      <Icon size={48} />
                    </Box>
                  </Stack>
                </CardContent>
                {/* Decorative circle */}
                <Box
                  sx={{
                    position: "absolute",
                    top: -20,
                    right: -20,
                    width: 100,
                    height: 100,
                    borderRadius: "50%",
                    bgcolor: "rgba(255,255,255,0.1)",
                  }}
                />
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Recent & Pending Lists */}
      <Grid container spacing={3} sx={{ justifyContent: "space-between" }}>
        {/* Latest Users */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              p: 0,
              borderRadius: 3,
              height: "100%",
              overflow: "hidden",
              boxShadow: 2,
            }}
          >
            <Box
              sx={{ p: 3, borderBottom: "1px solid", borderColor: "divider" }}
            >
              <Typography variant="h6" fontWeight={700}>
                Latest Registrations
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {apiData?.recentUsers?.slice(0, 5).map((user, index) => (
                <React.Fragment key={user.id}>
                  <ListItem sx={{ px: 3, py: 2 }}>
                    <ListItemAvatar>
                      <Avatar
                        src={
                          Array.isArray(user.avatar)
                            ? `${apiEndpoints.image}${user.avatar[0]}`
                            : `${apiEndpoints.image}${user.avatar}`
                        }
                        alt={user.name}
                      >
                        {user.name?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="subtitle2" fontWeight={600}>
                            {user.name && user.name.length > 20
                              ? user.name.substring(0, 20) + "..."
                              : user.name || "Unknown"}
                          </Typography>
                          <Chip
                            label={user.status}
                            size="small"
                            color={getStatusColor(user.status)}
                            variant="outlined"
                            sx={{ height: 20, fontSize: "0.65rem" }}
                          />
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {user.location || "No location"} •{" "}
                            {getTimeAgo(user.joinedAt)}
                          </Typography>
                        </React.Fragment>
                      }
                    />
                  </ListItem>
                  {index < 4 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Pending Users */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper
            sx={{
              p: 0,
              borderRadius: 3,
              height: "100%",
              overflow: "hidden",
              boxShadow: 2,
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Clock size={20} />
              <Typography variant="h6" fontWeight={700}>
                Pending Verifications
              </Typography>
            </Box>
            <List sx={{ p: 0 }}>
              {apiData?.pendingUsers?.slice(0, 5).map((user, index) => (
                <React.Fragment key={user.id}>
                  <ListItem sx={{ px: 3, py: 2 }}>
                    <ListItemAvatar>
                      <Avatar
                        src={
                          Array.isArray(user.avatar)
                            ? user.avatar[0]
                            : user.avatar
                        }
                        sx={{ bgcolor: "warning.light", color: "warning.dark" }}
                      >
                        {user.name?.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" fontWeight={600}>
                          {user.name && user.name.length > 20
                            ? user.name.substring(0, 20) + "..."
                            : user.name || "Unknown"}
                        </Typography>
                      }
                      secondary={user.location || "Location unknown"}
                    />
                    <Chip
                      label="Review"
                      size="small"
                      color="warning"
                      onClick={() => {}}
                      sx={{ cursor: "pointer" }}
                    />
                  </ListItem>
                  {index < 4 && <Divider variant="inset" component="li" />}
                </React.Fragment>
              ))}
              {(!apiData?.pendingUsers ||
                apiData?.pendingUsers.length === 0) && (
                <Box
                  sx={{ p: 4, textAlign: "center", color: "text.secondary" }}
                >
                  <Typography variant="body2">No pending users</Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
